// Native Mapbox IControl: a vertical group of POI category toggle buttons,
// styled like Mapbox's own controls and added at 'top-left' (under the zoom
// controls). It owns the custom POI layer imperatively — registers the pin
// icons, adds the `vansite-poi` symbol layer, wires the click→name popup, and
// re-applies the category filter — while sharing state through the Redux duck
// (so toggles stay in sync across every map).
import { selectActivePoiCategories, togglePoiCategory } from '../../ducks/poiFilters.duck';
import {
  applyPoiCategoryFilter,
  CUSTOM_POI_LAYER,
  ensureCustomPoiLayer,
  POI_CATEGORIES,
} from '../../util/maps/poiCategories';
import { buildDirectionsUrl, isAndroidDevice } from '../../util/maps/directionsLink';
import { ensurePoiIcons, POI_COLORS, POI_GLYPHS } from '../../util/maps/poiPinIcons';

// Glyph-only SVG (no teardrop), recolored by the button via currentColor.
const buttonGlyphSvg = (glyph) =>
  `<svg width="18" height="18" viewBox="-12 -12 24 24" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" ` +
  `stroke-linejoin="round" aria-hidden="true">${glyph}</svg>`;

class PoiLayerControl {
  constructor({ store, intl } = {}) {
    this._store = store;
    this._intl = intl;
    this._map = null;
    this._container = null;
    this._buttons = {};
    this._unsubscribe = null;
    this._popup = null;
    this._ready = false;
    this._lastActive = undefined;
    this._setup = this._setup.bind(this);
    this._onStoreChange = this._onStoreChange.bind(this);
    this._onLayerClick = this._onLayerClick.bind(this);
    this._onLayerEnter = this._onLayerEnter.bind(this);
    this._onLayerLeave = this._onLayerLeave.bind(this);
  }

  getDefaultPosition() {
    return 'top-left';
  }

  onAdd(map) {
    this._map = map;

    const container = document.createElement('div');
    container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    container.setAttribute('aria-label', this._label('groupLabel'));

    POI_CATEGORIES.forEach((cat) => {
      const button = document.createElement('button');
      button.type = 'button';
      const title = this._label(cat.id);
      button.title = title;
      button.setAttribute('aria-label', title);
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = buttonGlyphSvg(POI_GLYPHS[cat.id] ?? POI_GLYPHS.default);
      button.style.display = 'flex';
      button.style.alignItems = 'center';
      button.style.justifyContent = 'center';
      button.addEventListener('click', () => this._store.dispatch(togglePoiCategory(cat.id)));
      this._buttons[cat.id] = button;
      container.appendChild(button);
    });

    this._container = container;

    if (map.isStyleLoaded && map.isStyleLoaded()) this._setup();
    else map.once('style.load', this._setup);

    this._lastActive = this._activeCategories();
    this._unsubscribe = this._store.subscribe(this._onStoreChange);
    this._renderActiveButtons();

    return container;
  }

  onRemove() {
    const map = this._map;
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    if (map) {
      map.off('style.load', this._setup);
      if (this._ready) {
        map.off('click', CUSTOM_POI_LAYER, this._onLayerClick);
        map.off('mouseenter', CUSTOM_POI_LAYER, this._onLayerEnter);
        map.off('mouseleave', CUSTOM_POI_LAYER, this._onLayerLeave);
      }
    }
    if (this._popup) {
      this._popup.remove();
      this._popup = null;
    }
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    this._map = null;
    this._ready = false;
  }

  async _setup() {
    const map = this._map;
    if (!map || this._ready) return;
    await ensurePoiIcons(map);
    if (!this._map) return; // control removed during the async load
    ensureCustomPoiLayer(map);
    applyPoiCategoryFilter(map, this._activeCategories());
    map.on('click', CUSTOM_POI_LAYER, this._onLayerClick);
    map.on('mouseenter', CUSTOM_POI_LAYER, this._onLayerEnter);
    map.on('mouseleave', CUSTOM_POI_LAYER, this._onLayerLeave);
    this._ready = true;
  }

  _onStoreChange() {
    // The selector returns a stable array reference unless SET_POI_CATEGORIES
    // ran, so bail on unrelated store dispatches instead of re-filtering and
    // restyling the buttons on every action (this control mounts on up to 3 maps).
    const active = this._activeCategories();
    if (active === this._lastActive) return;
    this._lastActive = active;
    if (this._map && this._ready) {
      applyPoiCategoryFilter(this._map, active);
    }
    this._renderActiveButtons();
  }

  _renderActiveButtons() {
    const active = this._activeCategories();
    POI_CATEGORIES.forEach((cat) => {
      const button = this._buttons[cat.id];
      if (!button) return;
      const on = active.includes(cat.id);
      const color = POI_COLORS[cat.id] ?? POI_COLORS.default;
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.style.color = on ? '#FFFFFF' : color;
      button.style.backgroundColor = on ? color : '';
    });
  }

  _onLayerClick(e) {
    const feature = e.features && e.features[0];
    if (!feature) return;
    const props = feature.properties || {};
    const name = props.name_de || props.name;
    if (!name) return;
    const coords =
      feature.geometry && feature.geometry.type === 'Point'
        ? feature.geometry.coordinates
        : [e.lngLat.lng, e.lngLat.lat];
    if (!this._popup) {
      this._popup = new window.mapboxgl.Popup({ offset: 24, closeButton: true });
    }
    this._popup
      .setLngLat(coords)
      .setDOMContent(this._popupContent(name, props.type, coords[1], coords[0]))
      .addTo(this._map);
  }

  // Built imperatively (this control lives outside React) and attached via
  // setDOMContent so the POI name is inserted as text, never parsed as HTML.
  _popupContent(name, type, lat, lng) {
    const root = document.createElement('div');
    const title = document.createElement('div');
    title.style.fontWeight = '600';
    title.textContent = name;
    root.appendChild(title);
    if (type) {
      const sub = document.createElement('div');
      sub.style.cssText = 'font-size:12px;opacity:0.7;margin-top:2px';
      sub.textContent = type;
      root.appendChild(sub);
    }
    // External Google Maps directions — a raw anchor is correct here (not the
    // app's internal Link component): it leaves the SPA for an external URL.
    const directionsUrl = buildDirectionsUrl({ lat, lng }, { android: isAndroidDevice(), label: name });
    if (directionsUrl) {
      const link = document.createElement('a');
      link.href = directionsUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = this._label('navigate');
      link.style.cssText = `display:inline-block;margin-top:8px;color:${POI_COLORS.default};font-weight:600;text-decoration:underline`;
      root.appendChild(link);
    }
    return root;
  }

  _onLayerEnter() {
    if (this._map) this._map.getCanvas().style.cursor = 'pointer';
  }

  _onLayerLeave() {
    if (this._map) this._map.getCanvas().style.cursor = '';
  }

  _activeCategories() {
    return selectActivePoiCategories(this._store.getState());
  }

  _label(id) {
    const messageId = id === 'groupLabel' ? 'PoiFilter.groupLabel' : `PoiFilter.${id}`;
    return this._intl ? this._intl.formatMessage({ id: messageId }) : id;
  }
}

export default PoiLayerControl;
